/**
 * Shared search query understanding: normalize, tokenize, light inflection.
 * Alias lookup stays in search-alias-map (imports these helpers).
 */

/**
 * Normalize a user search string for matching:
 * lowercase, strip punctuation to spaces, collapse whitespace.
 * "times-table!" / "Times  Tables" → "times table" / "times tables"
 */
export function normalizeSearchQuery(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9\s]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Grade + merchandising tags stay in CMS but must not participate in search matching.
 * Names are compared case-insensitively after normalizeSearchQuery-style lowercasing.
 */
const EXCLUDED_SEARCH_TAG_NAMES = new Set([
  "k",
  "pre-k",
  "prek",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "11",
  "12",
  "adult",
  "new",
  "popular",
]);

/** True when a tag name should be ignored by catalog search. */
export function isExcludedSearchTag(name: string | null | undefined): boolean {
  if (!name) return true;
  const key = name.trim().toLowerCase();
  if (!key) return true;
  if (EXCLUDED_SEARCH_TAG_NAMES.has(key)) return true;
  // "Pre-K" normalizes awkwardly if hyphen stripped elsewhere; accept common forms.
  if (key === "pre k") return true;
  return false;
}

/** Light English singular/plural variants for one token. */
export function wordInflectionVariants(word: string): string[] {
  if (word.length < 3) return [];
  const out = new Set<string>();

  if (word.endsWith("ies") && word.length > 4) {
    out.add(`${word.slice(0, -3)}y`);
  } else if (word.endsWith("es") && word.length > 3) {
    out.add(word.slice(0, -2));
    out.add(word.slice(0, -1));
  } else if (word.endsWith("s") && !word.endsWith("ss") && word.length > 3) {
    out.add(word.slice(0, -1));
  }

  if (!word.endsWith("s")) {
    out.add(`${word}s`);
    if (/[^aeiou]y$/i.test(word)) {
      out.add(`${word.slice(0, -1)}ies`);
    } else if (/(?:s|x|z|ch|sh)$/i.test(word)) {
      out.add(`${word}es`);
    }
  }

  out.delete(word);
  return [...out];
}

/** Phrase variants that only inflect the final word. */
export function lastWordInflectionCandidates(normalized: string): string[] {
  if (!normalized) return [];
  const words = normalized.split(" ");
  const last = words[words.length - 1];
  if (!last) return [];
  return wordInflectionVariants(last).map((variant) => [...words.slice(0, -1), variant].join(" "));
}

/** Tokens used for multi-word coverage scoring (drop 1-letter noise). */
export function tokenizeSearchQuery(normalized: string): string[] {
  if (!normalized) return [];
  return normalized.split(" ").filter((token) => token.length >= 2);
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Whole-word match (avoids "zz" matching inside "Puzzle"). */
function textHasWholeWord(haystack: string, word: string): boolean {
  if (!word) return false;
  return new RegExp(`(?:^|[^a-z0-9])${escapeRegExp(word)}(?:[^a-z0-9]|$)`, "i").test(haystack);
}

/** Prefix match on a word boundary (e.g. "pu" → "puzzle", but not "zz" → "puzzle"). */
function textHasWordPrefix(haystack: string, prefix: string): boolean {
  if (!prefix) return false;
  return new RegExp(`(?:^|[^a-z0-9])${escapeRegExp(prefix)}[a-z0-9]*`, "i").test(haystack);
}

/** True when haystack has token as a whole word, word prefix, or simple inflection. */
export function textMatchesToken(haystack: string, token: string): boolean {
  const text = haystack.toLowerCase();
  const t = token.toLowerCase();
  if (!t) return false;
  if (textHasWholeWord(text, t) || textHasWordPrefix(text, t)) return true;
  return wordInflectionVariants(t).some(
    (variant) => textHasWholeWord(text, variant) || textHasWordPrefix(text, variant)
  );
}

/** Whole-word match including simple inflections (no prefix: car ≠ Card). */
export function textMatchesWholeWordToken(haystack: string, token: string): boolean {
  const text = haystack.toLowerCase();
  const t = token.toLowerCase();
  if (!t) return false;
  if (textHasWholeWord(text, t)) return true;
  return wordInflectionVariants(t).some((variant) => textHasWholeWord(text, variant));
}

/** Flatten searchable game fields into one lowercase blob for token coverage. */
export function gameSearchBlob(game: {
  name?: string | null;
  slug?: string | null;
  tags?: Array<{ name?: string | null }> | null;
}): string {
  const tagNames = (game.tags ?? [])
    .map((tag) => tag.name ?? "")
    .filter((name) => name && !isExcludedSearchTag(name));
  const slugWords = (game.slug ?? "").replace(/-/g, " ");
  return [game.name ?? "", slugWords, ...tagNames].join(" ").toLowerCase();
}
